import { useMemo } from "react";
import { useShallow } from "zustand/react/shallow";
import { builderStateToDynamicFormSchema } from "../lib/builder-to-dynamic-schema";
import { useFormEditorStore } from "../state/form-editor-store";

/**
 * Memoized {@link DynamicFormSchema} for the current builder graph (preview / derived UI).
 * Threads the store's `formRules` into `schema.rules` so the runtime evaluator can fire
 * them in the preview modal.
 *
 * @param formId - Stable schema id (e.g. `ticket-type-form-${id}` or `job-type-form-${id}`).
 */
export function useFormEditorPreviewSchema(
	formId: string,
	previewDisabled?: boolean,
) {
	const {
		formTitle,
		formDescription,
		rootIds,
		nodes,
		formEditorEntity,
		formRules,
	} = useFormEditorStore(
		useShallow((s) => ({
			formTitle: s.formTitle,
			formDescription: s.formDescription,
			rootIds: s.rootIds,
			nodes: s.nodes,
			formEditorEntity: s.formEditorEntity,
			formRules: s.formRules,
		})),
	);
	return useMemo(
		() =>
			builderStateToDynamicFormSchema(
				{ formTitle, formDescription, rootIds, nodes },
				formId,
				{
					...(previewDisabled ? { previewDisabled: true as const } : {}),
					formEditorEntity,
					formRules,
				},
			),
		[
			formDescription,
			formTitle,
			rootIds,
			nodes,
			formId,
			previewDisabled,
			formEditorEntity,
			formRules,
		],
	);
}
